import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import { Clock, XCircle, AlertTriangle, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

const AccessGuard = ({ children }) => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-atlasCyan animate-pulse">{t('common.loading')}</div>
      </div>
    );
  }

  const status = user?.status || 'active';

  // Active users pass through
  if (status === 'active') {
    return children;
  }

  const statusConfig = {
    pending: { icon: Clock, color: 'text-atlasYellow', bgColor: 'bg-atlasYellow/10' },
    cancelled: { icon: AlertTriangle, color: 'text-atlasRed', bgColor: 'bg-atlasRed/10' },
    rejected: { icon: XCircle, color: 'text-atlasRed', bgColor: 'bg-atlasRed/10' },
  };

  const config = statusConfig[status] || statusConfig.pending;
  const Icon = config.icon;

  return (
    <div data-testid="access-guard" className="min-h-screen bg-atlasBg flex items-center justify-center px-6">
      <div className="text-center max-w-md">
        <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full ${config.bgColor} mb-6`}>
          <Icon className={`w-8 h-8 ${config.color}`} />
        </div>
        <h1 className="text-2xl font-bold text-white mb-3">{t(`accessGuard.${status}.title`)}</h1>
        <p className="text-atlasTextSecondary mb-8">{t(`accessGuard.${status}.desc`)}</p>
        <Button
          data-testid="access-guard-home-btn"
          onClick={() => navigate('/')}
          className="btn-primary flex items-center gap-2 mx-auto"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('accessGuard.cta')}
        </Button>
      </div>
    </div>
  );
};

export default AccessGuard;
